<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { PhPlayCircle } from '@phosphor-icons/vue'

import { usePageStore } from '@toni/rendering/src/stores/page'
import { useConfigStore } from '@toni/rendering/src/stores/config'
import { Teaser, TeaserContent, Figure, Picture, SectionTitleHeading, AdvertorialBadge, Badge } from '@toni/brand-ui'

import { isNativeTeaserItem } from '../../../../../components/ui/teaser/teaser-group-constants'
import NativeTeaserItem from '../../../../../components/ui/teaser/NativeTeaserItem.vue'
import TeaserSeparator from '../../content-blocks/teaser/TeaserSeparator.vue'
import Diagonal from '../../page-blocks/article-header/Diagonal.vue'
import { createImgSrc, createImgSrcSetArray, getImgDefinitions, setMediaQuery } from '../../../../../components/bauer-image/image-utils'

import type { TeaserGroupProps } from '../../../../../components/ui/teaser/use-teaser-groups'
import type { TeaserSchema } from '../../../../../utils/content-block-schema/teaser-schema'

const { t } = useI18n()

const props = withDefaults(defineProps<TeaserGroupProps>(), {
  variant: 'teaser-group-single-big',
  adaAutoNative: false
})

const brand = usePageStore().brandFromStore as string
const imageServiceUrl = useConfigStore().rsConfig.imageServiceUrl as string
const imageSrcDefinitions = useConfigStore().rsConfig.imageSrcDefinitions
const setName = computed(() => props.variant)
const teaserDefinitions = getImgDefinitions(brand, imageSrcDefinitions, setName.value)
const teaserWidth = computed(() => teaserDefinitions?.maxWidth)

const teaserSrc = (teaser: TeaserSchema) => {
  if (!teaser) return undefined
  return (createImgSrc({
    image: teaser,
    brand,
    imageServiceUrl,
    setName: setName.value,
    srcSetDefinition: {
      format: 'landscape'
    }
  }, teaserWidth.value))?.href ?? ''
}

const imgSrcSetArray = (teaser: TeaserSchema) => {
  return createImgSrcSetArray({
    image: teaser,
    brand,
    imageServiceUrl,
    setName: setName.value,
    imageSrcDefinitions
  })
}

const hasMultipleTeasers = computed(() => (props.teasers?.length ?? 0) > 1)

const headingType = computed(() => {
  return usePageStore().pageData?.pageMeta?.pageType === 'structureterm' ? 'h2' : 'div'
})

const teaserGroupStyleClasses = computed(() => {
  return {
    '[&:has(+[data-ad-slot-name])]:mb-0': props.variant === 'teaser-group-single-small',
    'flex flex-col sm:flex-row sm:gap-x-5 gap-y-5 w-full [&>*]:basis-1/2': props.variant === 'teaser-group-double-big',
    'flex w-full flex-col gap-y-5 sm:flex-row sm:gap-x-5': props.variant === 'teaser-group-double',
    'w-full sm:flex-1': props.variant === 'teaser-group-triple-first',
    'flex flex-col gap-5 sm:basis-1/3': props.variant === 'teaser-group-triple-other',
    'flex flex-col gap-5 sm:flex-row': props.variant === 'teaser-group-quadruple',
    'sm:basis-[calc(25%-20px)]': props.adaAutoNative && props.variant === 'teaser-group-quadruple'
  }
})

const teaserStyleClasses = computed(() => {
  return {
    'pb-2 md:pb-3': (props.variant === 'teaser-group-single-small') && hasMultipleTeasers.value,
    'sm:flex-1 sm:basis-1/4': !props.adaAutoNative && props.variant === 'teaser-group-quadruple',
    'relative flex flex-col': props.variant === 'teaser-group-single-big' || props.variant === 'teaser-group-double-big'
  }
})

const headingStyleClasses = computed(() => {
  return {
    'line-clamp-3': true,
    'text-foreground text-heading-display-sm sm:text-heading-disply-md md:text-heading-display-sm lg:text-heading-display-md': props.variant === 'teaser-group-single-big',
    'text-heading text-heading-xs md:text-heading-lg pl-3 md:pl-5': props.variant === 'teaser-group-single-small',
    'text-foreground text-heading-md md:text-heading-lg lg:text-heading-display-sm': props.variant === 'teaser-group-double-big',
    'text-heading text-heading-md md:text-heading-lg lg:text-heading-display-sm px-4 md:px-5': props.variant === 'teaser-group-double' || props.variant === 'teaser-group-triple-first',
    'text-heading text-heading-xs lg:text-heading-md px-4': props.adaAutoNative || props.variant === 'teaser-group-triple-other' || props.variant === 'teaser-group-quadruple'
  }
})

const figureStyleClasses = computed(() => {
  return {
    'basis-1/2': props.variant === 'teaser-group-single-small' || props.variant === 'teaser-group-triple-other' || props.variant === 'teaser-group-quadruple',
    'sm:flex-none': props.variant === 'teaser-group-triple-other' || props.variant === 'teaser-group-quadruple',
    'md:flex-none md:w-[33%]': props.variant === 'teaser-group-single-small'
  }
})

const teaserContentStyleClasses = computed(() => {
  return {
    'h-full justify-between': ['teaser-group-double', 'teaser-group-quadruple'].includes(props.variant),
    'top-0 sm:-top-3.5': props.adaAutoNative,
    'pt-5 pb-3 lg:pb-4 px-4 md:px-5 bg-accent-tertiary text-accent-tertiary': props.variant === 'teaser-group-single-big' || props.variant === 'teaser-group-double-big',
    'px-5': props.variant === 'teaser-group-single-big',
    'bottom-4 md:bottom-5': props.variant === 'teaser-group-double' || props.variant === 'teaser-group-triple-first',
    'bottom-0 sm:bottom-4 overflow-hidden': props.variant === 'teaser-group-triple-other' || props.variant === 'teaser-group-quadruple',
    'overflow-hidden': props.variant === 'teaser-group-single-small'
  }
})

const videoIconStyleClasses = computed(() => {
  return {
    'absolute h-8 w-9 z-10 bg-white flex items-center justify-center': true,
    'bottom-4 right-4': props.variant === 'teaser-group-single-big',
    'md:bottom-3 md:right-3 bottom-2 right-2': props.variant === 'teaser-group-single-small',
    'md:bottom-4 md:right-4 bottom-3 right-3': props.variant === 'teaser-group-double-big',
    '2xl:bottom-4 2xl:right-4 bottom-3 right-3': props.variant === 'teaser-group-double' || props.variant === 'teaser-group-triple-first',
    '2xl:bottom-3 2xl:right-3 bottom-2 right-2 ': props.variant === 'teaser-group-triple-other' || props.variant === 'teaser-group-quadruple'
  }
})

const advertorialBadgeStyleClasses = computed(() => {
  return {
    'absolute text-badge-advertisement text-foreground px-1 py-0.5 z-10 bg-background opacity-80 lowercase first-letter:uppercase': true,
    'top-4 right-4': props.variant === 'teaser-group-single-big',
    'md:top-3 md:right-3 top-2 right-2': props.variant === 'teaser-group-single-small',
    'md:top-4 md:right-4 top-3 right-3': props.variant === 'teaser-group-double-big',
    '2xl:top-4 2xl:right-4 top-3 right-3': props.variant === 'teaser-group-double' || props.variant === 'teaser-group-triple-first',
    '2xl:top-3 2xl:right-3 top-2 right-2 ': props.variant === 'teaser-group-triple-other' || props.variant === 'teaser-group-quadruple'
  }
})

const badgeStyleClasses = computed(() => {
  return {
    'line-clamp-1': true,
    'h-[46px] leading-[46px] lg:leading-[46px]': ['teaser-group-single-big'].includes(props.variant),
    'h-[32px] md:h-[46px] leading-[32px] md:leading-[46px]': ['teaser-group-single-small'].includes(props.variant),
    'h-[34px] md:h-[46px] leading-[34px] md:leading-[46px]': ['teaser-group-double-big', 'teaser-group-double', 'teaser-group-triple-first'].includes(props.variant),
    'h-[32px] leading-[32px] md:h-[34px] md:leading-[34px]': ['teaser-group-quadruple', 'teaser-group-triple-other'].includes(props.variant),
    'px-4': ['teaser-group-single-small', 'teaser-group-double-big', 'teaser-group-double', 'teaser-group-triple-first', 'teaser-group-triple-other', 'teaser-group-quadruple'].includes(props.variant),
    'px-5': ['teaser-group-single-big'].includes(props.variant),
    'md:px-5': ['teaser-group-single-small', 'teaser-group-double-big', 'teaser-group-double', 'teaser-group-triple-first'].includes(props.variant),
    'absolute bottom-[calc(100%-16px)] md:bottom-[calc(100%-21px)] left-0 z-10': props.variant === 'teaser-group-single-big' || props.variant === 'teaser-group-double-big',
    'text-badge-md lg:text-badge-lg bottom-[calc(100%-21px)]': props.variant === 'teaser-group-single-big',
    'text-badge-sm md:text-badge-lg': props.variant === 'teaser-group-single-small',
    'text-badge-md md:text-badge-lg': props.variant === 'teaser-group-double-big' || props.variant === 'teaser-group-double' || props.variant === 'teaser-group-triple-first',
    'text-badge-sm md:text-badge-md': props.adaAutoNative || props.variant === 'teaser-group-triple-other' || props.variant === 'teaser-group-quadruple'
  }
})

const aspectRatioClasses = computed(() => {
  return {
    'object-cover w-full rounded-image': true,
    'aspect-[2/3] sm:aspect-16/9 md:aspect-[8/3]': props.variant === 'teaser-group-single-big',
    'aspect-16/10': props.variant === 'teaser-group-double',
    'aspect-1/1': props.variant === 'teaser-group-double-big',
    'aspect-3/2 lg:aspect-16/9': props.variant === 'teaser-group-double',
    'aspect-3/2': props.variant === 'teaser-group-single-small' || props.variant === 'teaser-group-triple-first' || props.variant === 'teaser-group-triple-other' || props.variant === 'teaser-group-quadruple'
  }
})

const advertorialText = t('ads.advertorialBadge')
</script>

<template>
  <div :class="teaserGroupStyleClasses"
       :data-ems="adaAutoNative ? 'ems-native-teasercontainer' : null">
    <template v-for="teaser in teasers">
      <NativeTeaserItem v-if="isNativeTeaserItem(teaser)"
                        :key="`native-teaser-${teaser.fallback.assetId}`"
                        :native-teaser-index="teaser.nativeTeaserIndex">
        <Teaser v-bind="{
                  href: teaser.fallback.url ?? '#',
                  target: teaser.fallback.target || '_self',
                  rel: teaser.fallback.rel || 'noopener noreferrer',
                  variant
                }"
                :data-ems="adaAutoNative ? 'ems-native-teaserlink' : null"
                class="h-full">
          <Figure :class="figureStyleClasses">
            <Picture>
              <source v-for="(singleSrc, index) in imgSrcSetArray(teaser.fallback)"
                      :key="index + 'webp'"
                      type="image/webp"
                      :media="setMediaQuery(singleSrc.viewportWidth)"
                      :srcset="singleSrc.src">
              <img :src="teaserSrc(teaser.fallback)"
                   :alt="teaser.fallback.imageAltText || ''"
                   :data-ems="adaAutoNative ? 'ems-native-teaserimage' : null"
                   :class="aspectRatioClasses">
            </Picture>
            <AdvertorialBadge v-if="teaser.fallback.advertorial"
                              :class="advertorialBadgeStyleClasses">
              {{ advertorialText }}
            </AdvertorialBadge>
            <div v-if="teaser?.hasAutoPlay"
                 :class="videoIconStyleClasses">
              <PhPlayCircle class="w-6 h-6" />
            </div>
          </Figure>
          <TeaserContent :variant="variant"
                         :class="teaserContentStyleClasses">
            <Badge v-if="teaser.fallback.roofline || teaser.fallback.mainStructureName"
                   type="text"
                   variant="tertiary"
                   :title="teaser.fallback.roofline || teaser.fallback.mainStructureName"
                   :data-ems="adaAutoNative ? 'ems-native-teasersubheadline' : null"
                   :class="badgeStyleClasses"
                   :uppercase="false">
              {{ teaser.fallback.roofline || teaser.fallback.mainStructureName }}
            </Badge>
            <div class="flex-1 flex flex-col justify-between gap-2 overflow-hidden">
              <SectionTitleHeading v-if="teaser.fallback.titleShort || teaser.fallback.titleLong"
                                   :data-ems="adaAutoNative ? 'ems-native-teaserheadline' : null"
                                   :title="(teaser.fallback?.titleLong || teaser.fallback?.titleShort) ?? ''"
                                   :heading-type="headingType"
                                   :class="headingStyleClasses">
                {{ teaser.fallback.titleLong || teaser.fallback.titleShort }}
              </SectionTitleHeading>
              <TeaserSeparator :variant="props.variant" />
            </div>
          </TeaserContent>
        </Teaser>
      </NativeTeaserItem>
      <Teaser v-else-if="teaser"
              :key="`teaser-${teaser?.assetId}`"
              v-bind="{
                href: teaser?.url ?? '#',
                target: teaser?.target || '_self',
                rel: teaser?.rel || 'noopener noreferrer',
                variant
              }"
              :class="teaserStyleClasses"
              :data-ems="adaAutoNative ? 'ems-native-teaserlink' : null">
        <Figure :class="figureStyleClasses">
          <Picture>
            <template v-if="!adaAutoNative && !teaser?.externalImageSrc">
              <source v-for="(singleSrc, index) in imgSrcSetArray(teaser)"
                      :key="index + 'webp'"
                      type="image/webp"
                      :media="setMediaQuery(singleSrc.viewportWidth)"
                      :srcset="singleSrc.src">
            </template>
            <img :src="teaser.mockImgSrc === undefined ? teaserSrc(teaser) : teaser.mockImgSrc"
                 :alt="teaser.imageAltText || ''"
                 :data-ems="adaAutoNative ? 'ems-native-teaserimage' : null"
                 :class="aspectRatioClasses">
          </Picture>
          <AdvertorialBadge v-if="teaser?.advertorial"
                            :class="advertorialBadgeStyleClasses"
                            :data-ems="adaAutoNative ? 'ems-native-teaseradlabel' : null">
            {{ advertorialText }}
          </AdvertorialBadge>
          <div v-if="teaser?.hasAutoPlay"
               :class="videoIconStyleClasses">
            <PhPlayCircle class="w-6 h-6" />
          </div>
        </Figure>
        <TeaserContent :variant="variant"
                       :class="teaserContentStyleClasses">
          <Badge v-if="teaser?.roofline || teaser?.mainStructureName"
                 type="text"
                 variant="tertiary"
                 :title="teaser?.roofline || teaser?.mainStructureName"
                 :data-ems="adaAutoNative ? 'ems-native-teasersubheadline' : null"
                 :class="badgeStyleClasses"
                 :uppercase="false">
            {{ teaser.roofline || teaser.mainStructureName }}
          </Badge>
          <div class="flex-1 flex flex-col justify-between gap-2 overflow-hidden">
            <SectionTitleHeading v-if="teaser?.titleShort || teaser?.titleLong"
                                 :data-ems="adaAutoNative ? 'ems-native-teaserheadline' : null"
                                 :title="(teaser.titleLong || teaser.titleShort) ?? ''"
                                 :heading-type="headingType"
                                 :class="headingStyleClasses">
              {{ teaser.titleLong || teaser.titleShort }}
            </SectionTitleHeading>
            <div v-if="(['teaser-group-single-big', 'teaser-group-double-big'].includes(variant))"
                 class="absolute bottom-[98%] left-0 w-full leading-none text-accent-primary-background">
              <Diagonal class="w-full h-full"
                        :width="764"
                        :height="30"
                        :start-height="30" />
            </div>
            <TeaserSeparator :variant="props.variant" />
          </div>
        </TeaserContent>
      </Teaser>
    </template>
  </div>
</template>
